import { callGetExternalApi } from '@ctn/axios-util';

import { useSessionStore } from '@/store/session.store';

import { getUrlTramite } from '@/helpers/navigation';
import { type ConfigTramite } from '@/interfaces/model/don.interface';
import type {
	IMaestro,
	IMaestrosParams,
	IMunicipiosBDCorpo,
	IMunicipiosByCp,
	IPaisesBDCorpo,
	IProvinciasBDCorpo,
	ITiposViaBDCorpo,
	TipoDocumento,
} from '@/interfaces/model/maestros.interface';
import { type INotaria } from '@/interfaces/model/TramiteNuevo/INotaria.interface';
import axiosInstance, { axiosInstanceBdCorpo, axiosInstanceBdCorpoNotaria } from '@/utils/axiosInterceptor';

export const baseURLGeneric = '/maestros';

const getBaseUrlTramite = () => {
	const tramiteData = useSessionStore.getState().getTramiteData();

	if (!tramiteData) {
		throw new Error('No hay un trámite seleccionado para cargar sus maestros.');
	}

	const baseURLTramiteRelative = getUrlTramite(tramiteData);
	return `${baseURLTramiteRelative}/maestros`;
};

export const getBackendUrlBdCorpo = ({ idMunicipio, codigoPostal, idNotaria }: IMaestrosParams) => ({
	GET_TIPO_VIA: '/tipo-via',
	GET_PAISES: '/paises',
	GET_PROVINCIAS: '/provincias',
	GET_MUNICIPIOS: '/municipios',
	GET_MUNICIPIOS_BY_PROVINCIA: `/provincias/${idMunicipio}/municipios`,
	GET_MUNICIPIOS_BY_CP: `/codigos-postales/${codigoPostal}`,
	GET_NOTARIA: `/notaria/${idNotaria}`,
	GET_NOTARIOS: '/notarias/buscar'
});

export const getBackendUrlGeneral = (params: IMaestrosParams) => ({
	GET_MAESTRO_ACCION: `${baseURLGeneric}/accion`,
	GET_MAESTRO_EMISOR: `${baseURLGeneric}/emisor`,
	GET_MAESTRO_ESTADO_SOLICITUD: `${baseURLGeneric}/estado-solicitud`,
	GET_MAESTRO_ROL: `${baseURLGeneric}/rol`,
	GET_MAESTRO_TIPO_DIRECCION: `${baseURLGeneric}/tipo-direccion`,
	GET_MAESTRO_TIPO_DOCUMENTO: `${baseURLGeneric}/tipo-documento`,
	GET_MAESTRO_TIPO_DOCUMENTO_IDENT: `${baseURLGeneric}/tipo-documento-ident`,
	GET_MAESTRO_TIPO_EVENTO: `${baseURLGeneric}/tipo-evento`,
	GET_MAESTRO_TIPO_NOTIFICACION_TRAMITE: `${baseURLGeneric}/tipo-notificacion-tramite`,
	GET_MAESTRO_TIPO_TRAMITE: `${baseURLGeneric}/tipo-tramite`,
});

export const getBackendUrlTramite = (params: IMaestrosParams) => ({
	GET_MAESTRO_CANAL_NOTIFICACION: `${getBaseUrlTramite()}/canal-notificacion`,
	GET_MAESTRO_ESTADO_SUBSANACION: `${getBaseUrlTramite()}/estado-subsanacion`,
	GET_MAESTRO_ESTADO_TRAMITE: `${getBaseUrlTramite()}/estado-tramite`,
	GET_MAESTRO_TIPO_COMUNICACION_EMISOR: `${getBaseUrlTramite()}/tipo-comunicacion-emisor`,
	GET_MAESTRO_TIPO_COMUNICACION_PLATON: `${getBaseUrlTramite()}/tipo-comunicacion-platon`,
	GET_MAESTRO_PON_TIPO_DOCUMENTO_IDENT: `${getBaseUrlTramite()}/tipo-documento-ident`,
	GET_MAESTRO_PON_TIPO_DOCUMENTO: `${getBaseUrlTramite()}/tipo-documento`,
	GET_MAESTRO_PON_TIPO_EVENTO: `${getBaseUrlTramite()}/tipo-evento`,
	GET_MAESTRO_TIPO_INTERVENCION: `${getBaseUrlTramite()}/tipo-intervencion`,
	GET_MAESTRO_TIPO_ORIGEN: `${getBaseUrlTramite()}/tipo-origen`,
	GET_MAESTRO_TIPO_PERSONA: `${getBaseUrlTramite()}/tipo-persona`,
	GET_MAESTRO_TIPO_SUBSANACION: `${getBaseUrlTramite()}/tipo-subsanacion`,
	GET_MAESTRO_CONFIGURACION_TRAMITE: `${getBaseUrlTramite()}/configuracion-tramite`,
});

// --- Funciones de Servicio de Maestros de GENERAL ---

export const getMaestroAccion = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_ACCION,
	});
export const getMaestroEmisor = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_EMISOR,
	});
export const getMaestroEstadoSolicitud = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_ESTADO_SOLICITUD,
	});
export const getMaestroRol = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_ROL,
	});
export const getMaestroTipoDireccion = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_TIPO_DIRECCION,
	});
export const getMaestroTipoDocumento = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_TIPO_DOCUMENTO,
	});
export const getMaestroTipoDocumentoIdent = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_TIPO_DOCUMENTO_IDENT,
	});
export const getMaestroTipoEvento = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_TIPO_EVENTO,
	});
export const getMaestroTipoNotificacionTramite = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_TIPO_NOTIFICACION_TRAMITE,
	});
export const getMaestroTipoTramite = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlGeneral({}).GET_MAESTRO_TIPO_TRAMITE,
	});

export const maestrosPNPServices = {
	accion: getMaestroAccion,
	emisor: getMaestroEmisor,
	estadoSolicitud: getMaestroEstadoSolicitud,
	rol: getMaestroRol,
	tipoDireccion: getMaestroTipoDireccion,
	tipoDocumento: getMaestroTipoDocumento,
	tipoDocumentoIdent: getMaestroTipoDocumentoIdent,
	tipoEvento: getMaestroTipoEvento,
	tipoNotificacionTramite: getMaestroTipoNotificacionTramite,
	tipoTramite: getMaestroTipoTramite,
};

// --- FUNCIONES DE SERVICIO BDCorpo ---

export const getTipoVia = async () =>
	callGetExternalApi<ITiposViaBDCorpo[]>({
		axiosInstance: axiosInstanceBdCorpo,
		withCredentials: true,
		url: getBackendUrlBdCorpo({}).GET_TIPO_VIA,
	});
export const getPaises = async () =>
	callGetExternalApi<IPaisesBDCorpo[]>({
		axiosInstance: axiosInstanceBdCorpo,
		withCredentials: true,
		url: getBackendUrlBdCorpo({}).GET_PAISES,
	});
export const getProvincias = async () =>
	callGetExternalApi<IProvinciasBDCorpo[]>({
		axiosInstance: axiosInstanceBdCorpo,
		withCredentials: true,
		url: getBackendUrlBdCorpo({}).GET_PROVINCIAS,
	});
export const getMunicipios = async () =>
	callGetExternalApi<IMunicipiosBDCorpo[]>({
		axiosInstance: axiosInstanceBdCorpo,
		withCredentials: true,
		url: getBackendUrlBdCorpo({}).GET_MUNICIPIOS,
	});
export const getMunicipiosByProvincia = async (idMunicipio = '07') =>
	callGetExternalApi<IMunicipiosBDCorpo[]>({
		axiosInstance: axiosInstanceBdCorpo,
		withCredentials: true,
		url: getBackendUrlBdCorpo({ idMunicipio }).GET_MUNICIPIOS_BY_PROVINCIA,
	});
export const getMunicipiosByCp = async (codigoPostal?: string) =>
	callGetExternalApi<IMunicipiosByCp>({
		axiosInstance: axiosInstanceBdCorpo,
		withCredentials: true,
		url: getBackendUrlBdCorpo({ codigoPostal }).GET_MUNICIPIOS_BY_CP,
	});

export const getNotarios = async (codigoPostal?: string, municipio?: string, provincia?: string, nombre?: string, apellidos?: string) =>
	callGetExternalApi<{ listado: INotaria[]; totalResults: number}>({
		axiosInstance: axiosInstanceBdCorpoNotaria,
		withCredentials: true,
		url: getBackendUrlBdCorpo({}).GET_NOTARIOS,
		headers: {
			...(codigoPostal && {codigoPostal}),
			...(municipio && {municipio}),
			...(provincia && {provincia}),
			...(nombre && { nombre }),
			...(apellidos && { apellidos })
		}
	});

export const getNotaria = async (idNotaria: string) =>
	callGetExternalApi<INotaria>({
		axiosInstance: axiosInstanceBdCorpoNotaria,
		withCredentials: true,
		url: getBackendUrlBdCorpo({idNotaria}).GET_NOTARIA
	});

export const maestrosBdCorpoServices = {
	tipoVia: getTipoVia,
	paises: getPaises,
	provincias: getProvincias,
};
// --- Funciones de Servicio de Maestros de PON ---

export const getMaestroPodCanalNotificacion = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_CANAL_NOTIFICACION,
	});

export const getMaestroPodEstadoSubsanacion = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_ESTADO_SUBSANACION,
	});

export const getMaestroPodEstadoTramite = async () => callGetExternalApi<IMaestro[]>({
	axiosInstance,
	withCredentials: true,
	url: getBackendUrlTramite({}).GET_MAESTRO_ESTADO_TRAMITE,
});


export const getMaestroPodTipoComunicacionEmisor = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_TIPO_COMUNICACION_EMISOR,
	});

export const getMaestroPodTipoComunicacionPlaton = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_TIPO_COMUNICACION_PLATON,
	});

export const getMaestroPodTipoDocumentoIdent = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_PON_TIPO_DOCUMENTO_IDENT,
	});

export const getMaestroPodTipoDocumento = async () =>
	callGetExternalApi<TipoDocumento[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_PON_TIPO_DOCUMENTO,
	});

export const getMaestroPodTipoEvento = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_PON_TIPO_EVENTO,
	});

export const getMaestroPodTipoIntervencion = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_TIPO_INTERVENCION,
	});

export const getMaestroPodTipoOrigen = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_TIPO_ORIGEN,
	});

export const getMaestroPodTipoPersona = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_TIPO_PERSONA,
	});

export const getMaestroPodTipoSubsanacion = async () =>
	callGetExternalApi<IMaestro[]>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_TIPO_SUBSANACION,
	});

export const getMaestroConfiguracionTramite = async () =>
	callGetExternalApi<ConfigTramite>({
		axiosInstance,
		withCredentials: true,
		url: getBackendUrlTramite({}).GET_MAESTRO_CONFIGURACION_TRAMITE,
	});

export const maestrosTramiteServices = {
	canalNotificacion: getMaestroPodCanalNotificacion,
	estadoSubsanacion: getMaestroPodEstadoSubsanacion,
	estadoTramite: getMaestroPodEstadoTramite,
	tipoComunicacionEmisor: getMaestroPodTipoComunicacionEmisor,
	tipoComunicacionPlaton: getMaestroPodTipoComunicacionPlaton,
	tipoDocumentoIdent: getMaestroPodTipoDocumentoIdent,
	tipoDocumento: getMaestroPodTipoDocumento,
	tipoEvento: getMaestroPodTipoEvento,
	tipoIntervencion: getMaestroPodTipoIntervencion,
	tipoOrigen: getMaestroPodTipoOrigen,
	tipoPersona: getMaestroPodTipoPersona,
	tipoSubsanacion: getMaestroPodTipoSubsanacion,
	configuracionTramite: getMaestroConfiguracionTramite,
};
